import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const LOGO_NAV = "/images/dea-wordmark.png";
const LOGO_HERO = "/images/dea-wordmark.png";

export default function TermsOfService() {
  return (
    <>
      <SEO 
        title="Terms of Service | Dea"
        description="Terms of Service for AmbitionGPT - The Ambition Brand"
      />
      <div className="min-h-screen bg-[#FFFCF7]">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-[#FFFCF7]/95 backdrop-blur-lg border-b border-[#EAD8C2]/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-[#AA9283] hover:text-[#726158]">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <main className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              <div className="text-center mb-12">
                <h1 className="font-serif text-4xl sm:text-5xl text-[#726158] mb-4">
                  Terms of Service
                </h1>
                <p className="text-[#AA9283]">
                  The Ambition Brand / AmbitionGPT
                </p>
                <p className="text-sm text-[#B1897C] mt-2">
                  hello@theambitionplanner.com | beambitious.chat
                </p>
              </div>

              <div className="glass-card rounded-2xl p-8 space-y-8">
                {/* Intro */}
                <section>
                  <p className="text-[#AA9283]">
                    By creating an account or purchasing access to AmbitionGPT, you agree to these Terms. If you do not agree, do not use our service. AmbitionGPT is operated by The Ambition Brand.
                  </p>
                </section>

                {/* 1. What We Provide */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">1. What We Provide</h2>
                  <p className="text-[#AA9283]">
                    AmbitionGPT is an AI-powered business coaching platform featuring Dea, an AI coach trained on the expertise of Deon Stokes. The service includes daily accountability, personalized 90-day goal roadmaps, cycle-synced task recommendations, and progress tracking.
                  </p>
                  <p className="text-[#AA9283] mt-3 font-medium">
                    AmbitionGPT is NOT a substitute for licensed therapy, legal, financial, or medical advice. Dea is AI-powered, not a human coach.
                  </p>
                </section>

                {/* 2. Pricing & Payment */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">2. Pricing & Payment</h2>
                  <ul className="space-y-2 text-[#AA9283]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">7-Day Power Start:</span>
                      <span>$9 one-time — immediate access, no auto-renewal</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Full Lifetime Access:</span>
                      <span>$97 one-time (or $88 when upgrading from trial)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Power Bundle:</span>
                      <span>$147 one-time — Full Access + Notion integration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#CAAB84] font-medium">Ultimate Bundle:</span>
                      <span>$297 one-time — Full Access + Notion + Clique.ish community</span>
                    </li>
                  </ul>
                  <p className="text-[#AA9283] mt-4">
                    All payments are processed securely by Stripe. We do not store your payment information. All purchases are one-time; there are no recurring charges. You are responsible for any applicable taxes based on your location.
                  </p>
                </section>

                {/* 3. Trial Credit */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">3. Trial Credit</h2>
                  <p className="text-[#AA9283]">
                    Your $9 Power Start payment applies as credit toward full access. Upgrade price is $88 (saving $9). This credit does not expire and is non-transferable.
                  </p>
                </section>

                {/* 4. Refund Policy */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">4. Refund Policy</h2>
                  <ul className="space-y-3 text-[#AA9283]">
                    <li>
                      <span className="text-[#CAAB84] font-medium">7-Day Power Start ($9):</span>
                      <span className="ml-1">Non-refundable. You receive immediate access upon purchase.</span>
                    </li>
                    <li>
                      <span className="text-[#CAAB84] font-medium">Full Access & Bundles:</span>
                      <span className="ml-1">7-day money-back guarantee. Email hello@theambitionplanner.com within 7 days of purchase. Refunds processed within 5–10 business days.</span>
                    </li>
                  </ul>
                  <p className="text-[#AA9283] mt-3">
                    All sales are final after 7 days. We reserve the right to deny refunds for suspected fraud, Terms violations, or repeated purchase-and-refund abuse.
                  </p>
                </section>

                {/* 5. Acceptable Use */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">5. Acceptable Use</h2>
                  <p className="text-[#AA9283] mb-3">You agree NOT to:</p>
                  <ul className="space-y-1 text-[#AA9283] list-disc list-inside">
                    <li>Share account credentials or resell access to the service</li>
                    <li>Use AmbitionGPT to train competing AI models or scrape our platform</li>
                    <li>Attempt to reverse engineer, hack, or compromise our systems</li>
                    <li>Use the service for illegal, harmful, or fraudulent purposes</li>
                  </ul>
                  <p className="text-[#AA9283] mt-3">
                    You are responsible for how you use AI-generated advice. Always consult licensed professionals for medical, legal, or financial decisions.
                  </p>
                </section>

                {/* 6. Intellectual Property */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">6. Intellectual Property</h2>
                  <p className="text-[#AA9283]">
                    All content, features, branding, and AI training behind AmbitionGPT are owned by The Ambition Brand. You retain ownership of your personal goals, reflections, and conversation history. You may export your data at any time by emailing <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>.
                  </p>
                </section>

                {/* 7. Account Termination */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">7. Account Termination</h2>
                  <p className="text-[#AA9283]">
                    You may delete your account at any time by emailing <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>. Your data will be permanently removed within 30 days. We may suspend or terminate accounts that violate these Terms, with notice when possible.
                  </p>
                </section>

                {/* 8. Disclaimers & Liability */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">8. Disclaimers & Liability</h2>
                  <p className="text-[#AA9283] font-medium mb-2">
                    THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.
                  </p>
                  <p className="text-[#AA9283]">
                    We do not guarantee specific business outcomes — your results depend on your effort and consistency. Our total liability is limited to the amount you paid in the past 12 months. We are not liable for indirect, incidental, or consequential damages.
                  </p>
                </section>

                {/* 9. Dispute Resolution */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">9. Dispute Resolution</h2>
                  <p className="text-[#AA9283]">
                    These Terms are governed by the laws of the State of Georgia. Disputes over $10,000 will be resolved through binding arbitration. Disputes under $10,000 may be brought in small claims court. You waive the right to participate in class action lawsuits.
                  </p>
                </section>

                {/* 10. Changes to These Terms */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">10. Changes to These Terms</h2>
                  <p className="text-[#AA9283]">
                    We may update these Terms with 30 days' notice to existing users via email or in-app notification. Continued use after changes constitutes acceptance. New users are bound by the current Terms immediately.
                  </p>
                </section>

                {/* Agreement */}
                <section className="pt-4 border-t border-[#EAD8C2]">
                  <p className="text-[#726158] font-medium text-center">
                    BY USING AMBITIONGPT, YOU AGREE TO THESE TERMS.
                  </p>
                </section>

                {/* Questions */}
                <section>
                  <h2 className="font-serif text-2xl text-[#726158] mb-4">Questions?</h2>
                  <p className="text-[#AA9283]">
                    <a href="mailto:hello@theambitionplanner.com" className="text-[#CAAB84] hover:underline">hello@theambitionplanner.com</a>
                  </p>
                </section>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 border-t border-[#EAD8C2] bg-[#FFFCF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <img src={LOGO_HERO} alt="Dea" className="h-12 w-auto" />
              <div className="flex items-center gap-4">
                <Link to="/privacy" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-[#B1897C]">|</span>
                <Link to="/terms" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Terms of Service
                </Link>
                <span className="text-[#B1897C]">|</span>
                <p className="text-[#B1897C] text-sm">
                  © 2026 The Ambition Brand. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
